import { Link } from "react-router-dom";
import { Ticket, Trophy } from "lucide-react";
import CountdownBar from "./CountdownBar";

function rarityColor(r) {
  if (r === "legendary") return "text-yellow-300 border-yellow-400/40 bg-yellow-400/5";
  if (r === "epic") return "text-fuchsia-300 border-fuchsia-400/40 bg-fuchsia-400/5";
  return "text-cyan-300 border-cyan-400/40 bg-cyan-400/5";
}

function formatGBP(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
}

export default function CompetitionCard({ c }) {
  const remaining = c.total_tickets - c.tickets_sold;
  const pct = Math.min(100, (c.tickets_sold / c.total_tickets) * 100);
  const ended = c.status !== "live" || (c.ends_at && new Date(c.ends_at) <= new Date());
  return (
    <Link
      to={`/competitions/${c.id}`}
      className="ufr-card rounded-md group block"
      data-testid={`competition-card-${c.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-black">
        <img
          src={c.image_url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-35"
        />
        <img
          src={c.image_url}
          alt={c.title}
          className={`absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-700 ${ended ? "opacity-60 grayscale-[35%]" : ""}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {ended ? (
            <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-bold border border-white/25 bg-black/60 text-white/70">Ended</span>
          ) : (
            <span className="ufr-chip">
              <span className="live-dot" /> Live
            </span>
          )}
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-bold border ${rarityColor(c.rarity)}`}>
            {c.rarity}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">
            {c.subtitle}
          </div>
          <div className="font-display text-lg md:text-xl text-white leading-tight mt-1">
            {c.title}
          </div>
        </div>
      </div>

      {!ended && <CountdownBar endsAt={c.ends_at} createdAt={c.created_at} />}

      <div className="p-4 space-y-3">
        {ended ? (
          <div className="border border-[color:var(--ufr-green)]/30 bg-[rgba(34,255,102,0.05)] rounded-md p-3" data-testid={`winner-banner-${c.id}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[color:var(--ufr-green)] font-bold">
              <Trophy size={12} /> Winner
            </div>
            <div className="font-display text-lg text-white mt-1">
              {c.winner?.user_name || "Drawn live"}{c.winner?.ticket_number != null ? ` — #${c.winner.ticket_number}` : ""}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">Tickets from</div>
              <div className="font-display text-2xl text-[color:var(--ufr-green)] glow-text">
                £{c.price_per_ticket.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="progress-track rounded-sm">
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className="text-white/60 flex items-center gap-1">
              <Ticket size={12} /> {ended ? `${c.tickets_sold.toLocaleString()} entries sold` : `${remaining.toLocaleString()} left of ${c.total_tickets.toLocaleString()}`}
            </span>
          </div>
        </div>

        <div className="pt-2">
          <div className={ended ? "btn-ufr-ghost text-center py-2.5 text-xs" : "btn-ufr-primary text-center py-2.5 text-xs"}>
            {ended ? "View result" : "Enter raffle"}
          </div>
        </div>
      </div>
    </Link>
  );
}
